// The tooltip over a reaction, like Discord's: the emoji large on the left, then ":name: reacted by Maya, Sam and
// 3 others". It appears after a short hover (at once when moving from one reaction to the next), floats over
// everything (fixed to the window, never inside the scrolling list), and sits above the reaction unless there's no
// room there (a message at the very top), then below; it's kept on screen sideways, with its arrow still pointing at
// the reaction.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { JUMBO_MARK, parseEmojiToken } from "@bloom/shared";
import { emojiReady, loadEmoji } from "../emoji.ts";
import { ReactionGlyph, useServerEmoji } from "../customEmoji.tsx";

/** How long the pointer rests on a reaction before its tooltip shows. */
export const TIP_DELAY_MS = 350;
/** Names listed before "and N others". */
const MAX_NAMES = 3;
/** Gap between the reaction and the tooltip, and the least room kept to the window's edges. */
const GAP = 8;
const EDGE = 8;

export interface ReactionTip {
  messageId: string;
  emoji: string;
  /** The reaction button, for placing the tooltip (read when it's drawn, so it follows the button). */
  anchor: HTMLElement;
}

/** ":joy:" for 😂 (once the emoji data is loaded), ":KEKL:" for a server emoji. */
function useEmojiName(emoji: string): string {
  const { byId } = useServerEmoji();
  const [, loaded] = useState(0);
  const t = parseEmojiToken(emoji);
  useEffect(() => {
    if (!t && !emojiReady()) void loadEmoji().then(() => loaded((n) => n + 1));
  }, [emoji]);
  if (t) return `:${byId.get(t.id)?.name ?? t.name}:`;
  const list = emojiReady();
  const bare = (s: string) => s.replace(/️/g, "");
  const hit = list?.find((e) => e.unicode === emoji) ?? list?.find((e) => bare(e.unicode) === bare(emoji));
  return hit?.shortcodes[0] ? `:${hit.shortcodes[0]}:` : emoji;
}

/**
 * "You", "You and Maya", "Maya, Sam and Jo", "Maya, Sam, Jo and 4 others". Each jumbo reaction (#714) carries its size
 * after the name: "bob, charlie and evan 3x".
 */
export function reactedBy(users: string[], names: Map<string, string>, me: string | null, jumbo: readonly string[] = []): string {
  const named = users.map((u) => ({ name: (u === me ? "You" : (names.get(u) ?? "Someone")) + (jumbo.includes(u) ? ` ${JUMBO_MARK}` : ""), you: u === me }));
  // You first, like Discord.
  named.sort((a, b) => Number(b.you) - Number(a.you));
  return join(named.map((n) => n.name));
}

/** The names as a sentence part, past MAX_NAMES "and N others". */
function join(named: string[]): string {
  if (named.length <= MAX_NAMES) return named.length > 1 ? `${named.slice(0, -1).join(", ")} and ${named.at(-1)}` : (named[0] ?? "");
  const rest = named.length - MAX_NAMES;
  return `${named.slice(0, MAX_NAMES).join(", ")} and ${rest} ${rest === 1 ? "other" : "others"}`;
}

/**
 * Where it goes: above the reaction, centred on it, when that fits inside `bounds` (the message list, so it doesn't
 * cover the header); else below; if neither fits, the side with more room in the window. Then kept inside the window
 * sideways. Returns the box and where along its edge the arrow points.
 */
export function placeTip(anchor: DOMRect, tip: { width: number; height: number }, bounds: { top: number; bottom: number }, view: { width: number; height: number }) {
  const above = anchor.top - GAP - tip.height;
  const below = anchor.bottom + GAP;
  const fitsAbove = above >= Math.max(bounds.top, 0) + EDGE;
  const fitsBelow = below + tip.height <= Math.min(bounds.bottom, view.height) - EDGE;
  const side: "above" | "below" = fitsAbove ? "above" : fitsBelow ? "below" : anchor.top > view.height - anchor.bottom ? "above" : "below";
  let top = side === "above" ? above : below;
  top = Math.min(Math.max(EDGE, top), view.height - EDGE - tip.height);
  const centre = anchor.left + anchor.width / 2;
  const left = Math.min(Math.max(EDGE, centre - tip.width / 2), view.width - EDGE - tip.width);
  // The arrow stays on the reaction even when the box had to slide over (but never past the rounded corners).
  const arrow = Math.min(Math.max(14, centre - left), tip.width - 14);
  return { top, left, side, arrow };
}

export function ReactionTooltip({ tip, moved, users, jumbo, names, me, bounds }: { tip: ReactionTip; moved: number; users: string[]; jumbo?: readonly string[]; names: Map<string, string>; me: string | null; bounds: () => DOMRect | null }) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<ReturnType<typeof placeTip> | null>(null);
  const name = useEmojiName(tip.emoji);
  const who = reactedBy(users, names, me, jumbo);

  // Measured, then placed, before it's painted (so it never flashes in the wrong spot).
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !tip.anchor.isConnected) return;
    const b = bounds();
    setPos(placeTip(tip.anchor.getBoundingClientRect(), { width: el.offsetWidth, height: el.offsetHeight }, { top: b?.top ?? 0, bottom: b?.bottom ?? innerHeight }, { width: innerWidth, height: innerHeight }));
  }, [tip, moved, name, who]);

  return createPortal(
    <div
      ref={box}
      className={`reaction-tip${pos ? ` ${pos.side}` : ""}${moved ? " settled" : ""}`}
      role="tooltip"
      style={pos ? { top: pos.top, left: pos.left, ["--arrow" as string]: `${pos.arrow}px` } : { top: -9999, left: -9999, visibility: "hidden" }}
    >
      <span className="reaction-tip-emoji" aria-hidden="true">
        <ReactionGlyph emoji={tip.emoji} />
      </span>
      <span className="reaction-tip-text">
        <strong>{name}</strong> reacted by {who}
      </span>
    </div>,
    document.body,
  );
}
